import { Box, type Theme } from '@mui/material';
import { useCallback, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { formatValue } from '../../../helpers';
import { useAnimatedNumber } from '../../../hooks/useAnimatedNumber';
import { useSkinStyles } from '../../../hooks/useSkinStyles';
import {
  JACKPOT_WINNER_ANIMATION_SKIN_STYLES,
  JACKPOT_WINNER_TIMING_MS,
  jackpotWinnerEntrance,
} from '../../../styles/components/jackpotWinnerAnimation';
import { mergeSx } from '../../../utils/styles';
import { FormattedBalance } from '../../atoms/FormattedBalance';

const COUNT_UP = { durationMs: JACKPOT_WINNER_TIMING_MS.countUp, delayMs: JACKPOT_WINNER_TIMING_MS.amount } as const;

// Sparkles burst outward on an ellipse that matches the wide amount, alternating accent and white.
const SPARKLE_COUNT = 12;
const SPARKLES = Array.from({ length: SPARKLE_COUNT }, (_, index) => {
  const angle = (index / SPARKLE_COUNT) * 2 * Math.PI + (index % 2 ? 0.2 : 0);
  const reach = index % 2 ? 0.8 : 1;
  const x = Math.cos(angle);
  const y = Math.sin(angle);

  return {
    style: {
      '--sparkle-from': `${x * 40}px, ${y * 12}px`,
      '--sparkle-to': `${x * 150 * reach}px, ${y * 44 * reach}px`,
      '--sparkle-size': `${index % 3 === 0 ? 6 : 4}px`,
      '--sparkle-delay': `${(index % 3) * 60}ms`,
    } as CSSProperties,
    isAccent: index % 2 === 0,
  };
});

const styles = {
  // Sits outside the clipping slot so the pop can scale and glow past the text box.
  pop: {
    position: 'relative',
    isolation: 'isolate',
    maxWidth: '100%',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: '-60% -15%',
      zIndex: -1,
      pointerEvents: 'none',
      background: 'radial-gradient(closest-side, currentColor, transparent)',
      opacity: 0,
    },
    '&[data-settled="true"]': {
      animation: `jackpotAmountPop ${JACKPOT_WINNER_TIMING_MS.pop}ms cubic-bezier(0.34, 1.56, 0.64, 1) both`,
    },
    '&[data-settled="true"]::before': {
      animation: `jackpotAmountGlow ${JACKPOT_WINNER_TIMING_MS.pop * 1.6}ms ease-out both`,
    },
    '@media (prefers-reduced-motion: reduce)': {
      '&[data-settled="true"], &[data-settled="true"]::before': { animation: 'none' },
    },
    '@keyframes jackpotAmountPop': {
      '0%': { transform: 'scale(1)', filter: 'brightness(1)' },
      '35%': { transform: 'scale(1.14)', filter: 'brightness(1.45)' },
      '65%': { transform: 'scale(0.97)', filter: 'brightness(1.1)' },
      '100%': { transform: 'scale(1)', filter: 'brightness(1)' },
    },
    '@keyframes jackpotAmountGlow': {
      '0%': { opacity: 0, transform: 'scale(0.6)' },
      '30%': { opacity: 0.3 },
      '100%': { opacity: 0, transform: 'scale(1.4)' },
    },
  },
  sparkle: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 'var(--sparkle-size)',
    height: 'var(--sparkle-size)',
    borderRadius: '50%',
    pointerEvents: 'none',
    opacity: 0,
    boxShadow: '0 0 8px currentColor',
    '[data-settled="true"] > &': {
      animation: `jackpotSparkle 900ms cubic-bezier(0.16, 1, 0.3, 1) var(--sparkle-delay) both`,
    },
    '@media (prefers-reduced-motion: reduce)': { display: 'none' },
    '@keyframes jackpotSparkle': {
      from: { opacity: 1, transform: 'translate(-50%, -50%) translate(var(--sparkle-from)) scale(1)' },
      to: { opacity: 0, transform: 'translate(-50%, -50%) translate(var(--sparkle-to)) scale(0.3)' },
    },
  },
  sparkleAccent: { backgroundColor: 'currentColor' },
  sparkleWhite: (theme: Theme) => ({ backgroundColor: theme.palette.common.white }),
  slot: {
    ...jackpotWinnerEntrance('jackpotAmountEnter', JACKPOT_WINNER_TIMING_MS.amount),
    position: 'relative',
    width: 'max-content',
    maxWidth: '100%',
    overflow: 'hidden',
    '@keyframes jackpotAmountEnter': {
      from: { opacity: 0, transform: 'translateY(14px) scale(0.94)' },
      to: { opacity: 1, transform: 'translateY(0) scale(1)' },
    },
  },
  amount: {
    display: 'block',
    textAlign: 'center',
    typography: 'h1',
    // Desktop shows the prize larger than any theme variant; the font still follows the skin.
    fontSize: { md: 40 },
    fontWeight: 800,
    lineHeight: 1.15,
    letterSpacing: '-1px',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  // Invisible final amount that sizes the slot, so the layout stays still while the visible amount counts up.
  reserve: {
    width: 'max-content',
    visibility: 'hidden',
  },
  visible: {
    position: 'absolute',
    inset: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
} as const;

const JackpotPrizeAmount = ({ amount }: { amount: number }) => {
  const skinStyles = useSkinStyles(JACKPOT_WINNER_ANIMATION_SKIN_STYLES);
  const { i18n } = useTranslation();
  const format = useCallback((value: number) => formatValue(value, i18n.language), [i18n.language]);
  const { ref, value, isSettled } = useAnimatedNumber<HTMLSpanElement>(amount, { ...COUNT_UP, format });

  return (
    <Box sx={mergeSx(styles.pop, skinStyles.amount)} data-settled={isSettled} data-testid='jackpot-amount-pop'>
      {SPARKLES.map(({ style, isAccent }, index) => (
        <Box
          key={index}
          aria-hidden='true'
          style={style}
          sx={mergeSx(styles.sparkle, isAccent ? styles.sparkleAccent : styles.sparkleWhite)}
        />
      ))}
      <Box sx={styles.slot} data-testid='jackpot-amount-slot'>
        <FormattedBalance
          balance={amount}
          noWrap
          aria-hidden='true'
          sx={mergeSx(styles.amount, skinStyles.amount, styles.reserve)}
          data-testid='jackpot-amount-reserve'
        />
        <FormattedBalance
          balance={amount}
          noWrap
          sx={mergeSx(styles.amount, skinStyles.amount, styles.visible)}
          data-testid='jackpot-amount'
          renderValue={() => (
            <span ref={ref} data-testid='jackpot-count-up'>
              {format(value)}
            </span>
          )}
        />
      </Box>
    </Box>
  );
};

export default JackpotPrizeAmount;
